import React from "react"
import { Modal, Form, Col, Row, Button } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import ActionButton from "../ActionButton"
import CSVUploadModal from "./CSVUploadModal"
import { LabAccountLookup } from "../../api/LabService/LabAccountsGet"
import { LabAccountCreate, LabCreate, LabUpload, LabAccountUpload } from "../../api/LabService/LabAccountCreate"
import { APIError } from "../../api/ObservableFromFetch"
import { ApiRootUrl } from "../../api/ApiUrl"
import { LabAccountMaxLength } from "../../config/Constants"

interface LabAccountSelectionProps {
  cancelText: string
  isLabSet: boolean
  isClinic: boolean
  isLocation: boolean
  implementationID?: number
  accountID?: number
  isLoggedIn?: boolean
  currentURL?: string
  onPrev: () => void
  onNext: () => void
  onUpdate: () => void
}

interface LabAccountSelectionState {
  showUploadSpreadsheet: boolean
  lab: string
  labAccount: string,
  labAccountCode?: string
  checking: boolean
  saving: boolean
}

export default class LabAccountSelection extends React.PureComponent<LabAccountSelectionProps, LabAccountSelectionState> {
  state: LabAccountSelectionState = {
    showUploadSpreadsheet: false,
    lab: "",
    labAccount: "",
    checking: false,
    saving: false
  }

  checkSubscription?: Subscription
  saveSubscription?: Subscription

  componentWillUnmount() {
    this.checkSubscription && this.checkSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }

  handleLabChange = (e: React.ChangeEvent<HTMLSelectElement>) => this.setState({ lab: e.target.value, labAccountCode: undefined })
  handleLabAccountChange = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ labAccount: e.target.value, labAccountCode: undefined })

  handleLabCheck = () => {
    this.setState({ checking: true })
    this.checkSubscription = LabAccountLookup(this.state.labAccount, this.state.lab, false, this.props.currentURL || "").subscribe(
      data => {
        this.setState({ labAccountCode: data[0].Code, checking: false })
        toast.success("Lab Account Found.")
      },
      error => this.handleAPIError(error, { checking: false })
    )
  }

  handleNext = () => {
    this.setState({ saving: true })
    this.saveSubscription && this.saveSubscription.unsubscribe()

    var params: LabCreate = {
      ImplementationClientFlag: this.props.isClinic ? "1" : this.props.isLocation ? "4" : "2",
      LabAccount: this.state.labAccount.toUpperCase(),
      LabCode: this.state.lab || ""
    }

    if (this.props.isLoggedIn) {
      params.ImplementationID = this.props.implementationID || 0
      params.AccountId = this.props.accountID || 0
    }
    else {
      params.Link = this.props.currentURL || ""
    }

    this.saveSubscription = LabAccountCreate(params).subscribe(
      (data) => {
        toast.success(data)
        this.setState({ lab: "", labAccount: "", labAccountCode: undefined, checking: false, saving: false })
        this.props.onUpdate()
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleOpenUpload = () => this.setState({ showUploadSpreadsheet: true })
  handleBulkUploaded = () => {
    this.setState({ showUploadSpreadsheet: false })
    this.props.onUpdate()
  }

  handleStartUploading = (fileName: string, fileContent: string) => {
    var params: LabUpload = {
      ImplementationClientFlag: this.props.isClinic ? "1" : "2",
      Base64String: fileContent
    }

    if (this.props.isLoggedIn) {
      params.ImplementationID = this.props.implementationID || 0
      if (!this.props.isClinic) { params.CompanyID = this.props.accountID || 0 }
    }
    else {
      params.Link = this.props.currentURL || ""
    }

    return LabAccountUpload(params)
  }

  render() {
    const { showUploadSpreadsheet, lab, labAccount, labAccountCode, checking, saving } = this.state
    return (<>
      <Modal backdrop="static" show size="xl">
        <Modal.Header>
          <Modal.Title className="text-uppercase">LAB ACCOUNT SELECTION</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row><Col><p>Please select your lab and then enter your lab account</p></Col></Row>
            <Form.Group as={Row} className="required">
              <Form.Label column sm={2} htmlFor="lab" className="pt-1">Select your Lab</Form.Label>
              <Col sm={8}>
                <Form.Control id="lab" as="select" onChange={this.handleLabChange} value={lab}>
                  <option value="">Select</option>
                  <option value="CRL">CRL</option>
                  <option value="PSY">Psychemedics</option>
                  <option value="OMEGA">Omega</option>
                  
                </Form.Control>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="required">
              <Form.Label column sm={2} htmlFor="labAccount" className="pt-1">Lab Account</Form.Label>
              <Col sm={8}>
                <Form.Control type="text" id="labAccount" autoComplete="off" value={labAccount} onChange={this.handleLabAccountChange} maxLength={LabAccountMaxLength}/>
              </Col>
              <Col sm={2}>
                <ActionButton variant="secondary" disabled={!lab || !labAccount} onClick={this.handleLabCheck} spin={checking} disableOnSpin>Check Account</ActionButton>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="required">
              <Col sm={{ span: 8, order: 2, offset: 2 }}>
                <Form.Control type="text" className="bg-white" readOnly value={labAccountCode || ""} />
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          {!this.props.isLocation && <Button variant="primary" onClick={this.handleOpenUpload}>Upload Spreadsheet</Button>}
          <div>
            {!this.props.isLoggedIn && <Button variant="primary" disabled={!this.props.isLabSet} onClick={this.props.onNext} className="mr-2">No More For Now</Button>}
            <Button variant="secondary" onClick={this.props.onPrev} className="mr-2">{this.props.cancelText}</Button>
            <ActionButton variant="primary" disabled={!labAccountCode} onClick={this.handleNext} spin={saving} disableOnSpin>Submit</ActionButton>
          </div>
        </Modal.Footer>
      </Modal>
      {showUploadSpreadsheet &&
        <CSVUploadModal show={true}
          title="CLINIC LABORATORY BULK UPLOAD"
          templateCSVUrl={ApiRootUrl() + "/Template/lab-account-template.csv"}
          onCancel={() => this.setState({ showUploadSpreadsheet: false })}
          onStartUpload={this.handleStartUploading}
          onUploaded={this.handleBulkUploaded}>
          <p>To create multiple lab codes for a company at once:<br />
                1. Create a Spreadsheet with the following columns:<br />
            <span className="pl-3">A. Laboratory (CRL)</span><br />
            <span className="pl-3">B. Lab Code</span><br />
                2. Save the Spreadsheet as a CSV text file.<br />
                3. Upload the file here.</p>
        </CSVUploadModal>}
    </>)
  }

  static defaultProps = { cancelText: "Previous", isLoggedIn: false }
}
